// Audit log viewer. Every mutation across Verso writes an audit_logs row —
// this page lets org admins query them by action prefix, resource type, or
// actor. Cursor-paginated ('Load more' fetches with ?before=<oldest_ts>).

import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { fmtDateTime } from '../lib/time.js';
import ListToolbar from '../components/shared/ListToolbar.jsx';

// Resource types + labels for the filter pills.
const RESOURCE_FILTERS = [
  { key: '',              label: 'All' },
  { key: 'subscriber',    label: 'Subscribers' },
  { key: 'location',      label: 'Locations' },
  { key: 'did',           label: 'DIDs' },
  { key: 'sip_account',   label: 'Extensions' },
  { key: 'device',        label: 'Devices' },
  { key: 'agent',         label: 'PBX Hosts' },
  { key: 'integration',   label: 'Integrations' },
  { key: 'user',          label: 'Users' },
  { key: 'invitation',    label: 'Invites' },
  { key: 'organization',  label: 'Org' },
];

function ActionPill({ action }) {
  // Color-code by the leading segment of the dotted action id.
  // subscriber.* = blue, location.* = green, did.* = purple, integration.* = amber, delete/soft_delete = red
  const [head] = action.split('.');
  const isDelete = /delete|soft_delete|remove/.test(action);
  const cls = isDelete
    ? 'bg-red-100 text-red-700'
    : ({
        subscriber:  'bg-blue-100 text-blue-700',
        location:    'bg-green-100 text-green-700',
        did:         'bg-purple-100 text-purple-700',
        integration: 'bg-amber-100 text-amber-700',
        device:      'bg-indigo-100 text-indigo-700',
      }[head] || 'bg-gray-100 text-gray-700');
  return (
    <span className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 font-mono text-xs font-medium ${cls}`}>
      {action}
    </span>
  );
}

function DetailsCell({ details }) {
  const entries = useMemo(() => {
    if (!details || typeof details !== 'object') return [];
    return Object.entries(details).filter(([k]) => k !== '_raw');
  }, [details]);
  if (entries.length === 0) return <span className="text-gray-300">—</span>;
  return (
    <div className="max-w-md">
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
        {entries.map(([k, v]) => (
          <span key={k} className="text-gray-500">
            <span className="text-gray-400">{k}:</span>{' '}
            <span className="font-mono text-gray-700">
              {typeof v === 'object' ? JSON.stringify(v) : String(v)}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function AuditLog() {
  // `search` is the raw input; `actionPrefix` is what actually feeds
  // the query. We debounce input → prefix (300ms) so we don't fire a
  // new server-side page on every keystroke, and reset paging when
  // either filter changes.
  const [search, setSearch] = useState('');
  const [actionPrefix, setActionPrefix] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setActionPrefix(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);
  const [resourceType, setResourceType] = useState('');
  const [pages, setPages] = useState([{ before: null }]);

  const filters = useMemo(
    () => ({ actionPrefix, resourceType }),
    [actionPrefix, resourceType],
  );

  // Filter changes reset paging so the operator doesn't see stitched
  // pages from a previous filter set.
  useEffect(() => { setPages([{ before: null }]); }, [actionPrefix, resourceType]);

  // TanStack Query key includes the filters + the last cursor so changes
  // trigger a refetch cleanly. We render all fetched pages appended.
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['audit-logs', filters, pages],
    queryFn: async () => {
      // Fire one request per page cursor and stitch the results in order.
      const results = [];
      for (const p of pages) {
        const params = { limit: 50 };
        if (p.before) params.before = p.before;
        if (filters.actionPrefix) params.action = filters.actionPrefix;
        if (filters.resourceType) params.resource_type = filters.resourceType;
        const r = await api.get('/audit-logs', params);
        results.push(r);
      }
      return results;
    },
  });

  const rows = data ? data.flatMap((page) => page.rows || []) : [];
  const lastPage = data && data.length > 0 ? data[data.length - 1] : null;
  const canLoadMore = Boolean(lastPage?.next_before);

  function loadMore() {
    if (!lastPage?.next_before) return;
    setPages([...pages, { before: lastPage.next_before }]);
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Audit Log</h1>
        <p className="mt-1 text-sm text-gray-500">
          Every mutation across subscribers, locations, DIDs, devices, and integrations. Newest first.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <ListToolbar
          className="flex-1"
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search action prefix (e.g. subscriber. or did.assign)…"
          filters={RESOURCE_FILTERS}
          activeFilter={resourceType}
          onFilterChange={setResourceType}
        />
        <button
          onClick={() => refetch()}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Refresh
        </button>
      </div>

      {/* Rows */}
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error.body?.error || error.message || 'Failed to load audit log'}
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-2 font-medium">When</th>
              <th className="px-4 py-2 font-medium">Action</th>
              <th className="px-4 py-2 font-medium">Resource</th>
              <th className="px-4 py-2 font-medium">Actor</th>
              <th className="px-4 py-2 font-medium">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-400">Loading…</td></tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-400">
                {filters.actionPrefix || filters.resourceType ? 'No matches for these filters' : 'No audit events yet'}
              </td></tr>
            )}
            {rows.map((row) => (
              <tr key={row.id} className="align-top">
                <td className="whitespace-nowrap px-4 py-2 text-gray-500">
                  {fmtDateTime(row.created_at)}
                </td>
                <td className="px-4 py-2"><ActionPill action={row.action} /></td>
                <td className="whitespace-nowrap px-4 py-2 text-gray-700">
                  <span className="text-xs uppercase tracking-wide text-gray-400">{row.resource_type}</span>
                  {row.resource_id && (
                    <span className="ml-1 font-mono text-xs text-gray-500">#{row.resource_id}</span>
                  )}
                </td>
                <td className="whitespace-nowrap px-4 py-2 text-gray-700">
                  {row.actor_name || row.actor_email || (
                    <span className="font-mono text-xs text-gray-400">{row.user_id}</span>
                  )}
                </td>
                <td className="px-4 py-2"><DetailsCell details={row.details} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {canLoadMore && (
        <div className="text-center">
          <button
            onClick={loadMore}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Load more
          </button>
        </div>
      )}
    </div>
  );
}
